import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import LoadingButton from "@mui/lab/LoadingButton"
import Box from "@mui/material/Box"
import CircularProgress from "@mui/material/CircularProgress"
import CssBaseline from "@mui/material/CssBaseline"
import Divider from "@mui/material/Divider"
import Paper from "@mui/material/Paper"
import Typography from "@mui/material/Typography"
import { useEffect, useMemo, useState } from "react"
import { useParams, useSearchParams } from "react-router-dom"

import Column from "../../common/components/Column"
import Row from "../../common/components/Row"
import { formatCurrencyLong } from "../../common/utils"
import AccountHeaderBar, {
  ACCOUNT_HEADER_OFFSET,
} from "../buy-mls/AccountHeaderBar"
import {
  createMlsPaymentLinkClient,
  linkErrorMessage,
} from "../buy-mls/linkClient"
import { MLS_ACTIVATION_TIMELINE } from "../customer/my-mls/mlsPurchaseActivationCopy"

const POLL_MS = 5000
const MAX_POLLS = 40

function Shell({ account = {}, children }) {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        pt: ACCOUNT_HEADER_OFFSET,
        bgcolor: "background.default",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
      }}
    >
      <CssBaseline />
      <AccountHeaderBar
        accountName={account.account_name}
        accountEmail={account.account_email}
      />
      <Paper variant="outlined" sx={{ p: 4, maxWidth: 520, width: "100%" }}>
        <Column sx={{ gap: 2 }}>{children}</Column>
      </Paper>
    </Box>
  )
}

function SummaryRow({ label, value }) {
  return (
    <Row sx={{ justifyContent: "space-between", gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ textAlign: "right" }}>
        {value}
      </Typography>
    </Row>
  )
}

function OrderSummary({ order }) {
  const names = (order.mls_names || []).join(", ")
  return (
    <Column sx={{ gap: 1 }}>
      {order.office_name ? (
        <SummaryRow label="Office" value={order.office_name} />
      ) : null}
      {order.contact_name ? (
        <SummaryRow label="Requested by" value={order.contact_name} />
      ) : null}
      {names ? <SummaryRow label="MLS" value={names} /> : null}
      <SummaryRow label="Quantity" value={order.quantity} />
      {order.credits_applied > 0 ? (
        <SummaryRow
          label="Covered by their plan"
          value={`${order.credits_applied} (no charge)`}
        />
      ) : null}
      {order.total_monthly != null ? (
        <>
          <Divider />
          <SummaryRow
            label="Monthly total"
            value={
              <strong>{formatCurrencyLong(order.total_monthly)}/month</strong>
            }
          />
        </>
      ) : null}
    </Column>
  )
}

function PaymentResult({ client, onOrder }) {
  const [order, setOrder] = useState(null)
  const [error, setError] = useState("")

  useEffect(() => {
    let cancelled = false
    let attempts = 0
    let timer = null
    const poll = () => {
      client
        .get("/billing/api/mls-payment/status/")
        .then(({ data }) => {
          if (cancelled) return
          setOrder(data)
          onOrder(data)
          if (data.status === "paid" || attempts >= MAX_POLLS) return
          attempts += 1
          timer = setTimeout(poll, POLL_MS)
        })
        .catch((err) => {
          if (cancelled) return
          if (err?.response?.status === 401) {
            setError(linkErrorMessage(err))
            return
          }
          if (attempts >= MAX_POLLS) return
          attempts += 1
          timer = setTimeout(poll, POLL_MS)
        })
    }
    poll()
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
    // onOrder is a state setter; polling restarts only for a new link.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client])

  if (error) return <Typography color="text.secondary">{error}</Typography>
  if (!order || order.status !== "paid") {
    return (
      <Column sx={{ alignItems: "center", gap: 2, textAlign: "center" }}>
        <CircularProgress />
        <Typography color="text.secondary">
          Confirming your payment… this can take a minute.
        </Typography>
      </Column>
    )
  }
  return (
    <Column sx={{ gap: 2 }}>
      <Column sx={{ alignItems: "center", gap: 1, textAlign: "center" }}>
        <CheckCircleOutlineIcon color="success" sx={{ fontSize: 48 }} />
        <Typography variant="h6" fontWeight={700}>
          Payment received — thank you!
        </Typography>
      </Column>
      <OrderSummary order={order} />
      <Typography variant="body2" color="text.secondary">
        {MLS_ACTIVATION_TIMELINE} You can close this tab.
      </Typography>
    </Column>
  )
}

/**
 * Standalone page for paying an MLS order someone else filled in. The link is
 * the only credential: it shows the order (never the MLS logins), opens a Zoho
 * checkout, and nothing in it can change the order or reach the app.
 */
export default function PayMlsPage() {
  const { signature: rawSignature } = useParams()
  const [searchParams] = useSearchParams()
  const signature = useMemo(
    () => decodeURIComponent(rawSignature || ""),
    [rawSignature]
  )
  const client = useMemo(
    () => createMlsPaymentLinkClient(signature),
    [signature]
  )
  const returnedFromCheckout = searchParams.get("mls_purchase") === "success"
  const [order, setOrder] = useState(null)
  const [message, setMessage] = useState("")
  const [paying, setPaying] = useState(false)
  const [payError, setPayError] = useState("")
  const [account, setAccount] = useState({})

  useEffect(() => {
    if (returnedFromCheckout) return undefined
    let cancelled = false
    client
      .get("/billing/api/mls-payment/")
      .then(({ data }) => {
        if (cancelled) return
        setOrder(data)
        setAccount(data)
        if (!data.payable) {
          setMessage(
            data.unavailable_reason || "This order can't be paid right now."
          )
        }
      })
      .catch((err) => {
        if (!cancelled) setMessage(linkErrorMessage(err))
      })
    return () => {
      cancelled = true
    }
  }, [client, returnedFromCheckout])

  function handlePay() {
    setPaying(true)
    setPayError("")
    client
      .post("/billing/api/mls-payment/checkout/")
      .then(({ data }) => {
        window.location.href = data.checkout_url
      })
      .catch((err) => {
        setPayError(
          err?.response?.data?.detail ||
            "Could not start checkout. Please try again."
        )
        setPaying(false)
      })
  }

  if (returnedFromCheckout) {
    return (
      <Shell account={account}>
        <PaymentResult client={client} onOrder={setAccount} />
      </Shell>
    )
  }

  if (message) {
    return (
      <Shell account={account}>
        <Typography variant="h6" fontWeight={700}>
          MLS order
        </Typography>
        <Typography color="text.secondary">{message}</Typography>
      </Shell>
    )
  }

  if (!order) {
    return (
      <Shell account={account}>
        <Row sx={{ justifyContent: "center" }}>
          <CircularProgress />
        </Row>
      </Shell>
    )
  }

  return (
    <Shell account={account}>
      <Typography variant="h6" fontWeight={700}>
        Pay for an MLS order
      </Typography>
      <Typography variant="body2" color="text.secondary">
        This order was set up for you to pay. Review it, then continue to our
        secure checkout.
      </Typography>
      <OrderSummary order={order} />
      {payError ? (
        <Typography variant="body2" color="error.main">
          {payError}
        </Typography>
      ) : null}
      <LoadingButton variant="contained" loading={paying} onClick={handlePay}>
        Pay
      </LoadingButton>
      <Typography variant="caption" color="text.secondary">
        Billed monthly; the amount is confirmed at checkout.
      </Typography>
    </Shell>
  )
}
